import React, { useState } from "react";
import { FaCheckCircle } from "react-icons/fa";
import { FiDownload, FiUpload } from "react-icons/fi";
import { IoAlertCircle } from "react-icons/io5";
import { toast } from "react-toastify";
import { useAuth } from "../../context/Auth";
import { BASE_URL } from "../../utils/endpoints";
import Swal from "sweetalert2";

const BranchBulkDrugUploadModal = ({
  isOpen,
  onClose,
  onUploadSuccess,
  branch_id,
}) => {
  const { requestWithAuth } = useAuth();
  const [file, setFile] = useState(null);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadResult, setUploadResult] = useState(null);
  const [dragActive, setDragActive] = useState(false);

  const handleFileSelect = (selectedFile) => {
    if (selectedFile && selectedFile.name.endsWith(".xlsx")) {
      setFile(selectedFile);
      setUploadResult(null);
    } else {
      alert("Please select a valid Excel file (.xlsx)");
    }
  };

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);

    const droppedFile = e.dataTransfer.files[0];
    if (droppedFile) {
      handleFileSelect(droppedFile);
    }
  };

  const handleUpload = async () => {
    if (!file) {
      toast.error("Please select a file first");
      return;
    }

    setIsUploading(true);
    const formData = new FormData();
    formData.append("drugs_list", file);
    formData.append("upload_type", "bulk");

    try {
      const response = await requestWithAuth(
        `/inventory/branch/${branch_id}/bulk-upload/`,
        "POST",
        formData,
        {
          "Content-Type": "mulitipart/form-data",
        }
      );

      setUploadResult(response);

      //   Swal.fire({
      //     icon: 'success',
      //     title: 'Success',
      //     text: `${response.data.successful_count} drugs uploaded successfully`,
      //   }).then(()=>{
      //     onClose();
      //   })
    } catch (error) {
      setUploadResult({
        error: "Network error occurred while uploading",
        upload_type: "bulk",
      });
    } finally {
      setIsUploading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full m-4 max-h-[90vh]">
        <div className="flex items-center justify-between p-6 border-b">
          <div className="flex items-center space-x-3">
            <h2 className="text-xl font-semibold text-gray-900">
              Bulk Drug Upload
            </h2>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <span className="h-6 w-6">X</span>
          </button>
        </div>

        {/* content */}
        <div className="p-6">
          <div className="mb-6">
            <h3 className="text-sm font-medium text-gray-900 mb-2">
              Instructions:
            </h3>
            <ul className="text-sm text-gray-600 space-y-1">
              <li>• Upload an Excel file (.xlsx) with drug information</li>
              <li>• Download and use the template provided </li>
              <li>• Required columns: name, reorder_level</li>
              <li>• All drugs will be created or none </li>
            </ul>
          </div>

          {/* DOWNLOAD TEMPLATE */}
          <div className="mb-6">
            <a
              href={`${BASE_URL}/media/BulkDrugUpload.xlsx`}
              download={"drug_upload_template.xlsx"}
              target="_blank"
              className="inline-flex items-center space-x-2 text-blue-600 hover:text-blue-800 text-sm font-medium"
            >
              <FiDownload className="h-4 w-4" />
              <span>Download Template</span>
            </a>
          </div>

          {/* FILE UPLOAD AREA */}
          <div
            className={`border-2 border-dashed rounded-lg p-8 text-center transition-colors ${dragActive
                ? "border-blue-400 bg-blue-50"
                : file
                  ? "border-green-400 bg-green-50"
                  : "border-gray-300 hover:border-gray-400"
              }`}
            onDragEnter={handleDrag}
            onDragLeave={handleDrag}
            onDragOver={handleDrag}
            onDrop={handleDrop}
          >
            {file ? (
              <div className="space-y-2">
                <FaCheckCircle className="h-12 w-12 text-green-500 mx-auto" />
                <p className="text-sm font-medium text-green-700">
                  {file.name}
                </p>
                <p className="text-xs text-gray-500">
                  {(file.size / 1024).toFixed(1)} KB
                </p>
                <button
                  onClick={() => setFile(null)}
                  className="text-xs text-red-600 hover:text-red-800"
                >
                  Remove file
                </button>
              </div>
            ) : (
              <div className="space-y-2">
                <FiUpload className="h-12 w-12 text-gray-400 mx-auto" />
                <p className="text-sm text-gray-600">
                  Drag and drop your Excel file here, or{" "}
                  <label className="text-blue-600 hover:text-blue-800 cursor-pointer font-medium">
                    browse
                    <input
                      type="file"
                      accept=".xlsx"
                      onChange={(e) => handleFileSelect(e.target.files[0])}
                      className="hidden"
                    />
                  </label>
                </p>
                <p className="text-xs text-gray-500">
                  Only .xlsx files are supported
                </p>
              </div>
            )}
          </div>

          {/* Upload Results */}
          {uploadResult && (
            <div className="mt-6">
              {uploadResult.error ? (
                <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                  <div className="flex items-start space-x-3">
                    <IoAlertCircle className="h-5 w-5 text-red-500 mt-0.5" />
                    <div className="flex-1">
                      <h4 className="text-sm font-medium text-red-800">
                        Upload Failed
                      </h4>

                      {uploadResult.message && (
                        <p className="text-xs text-red-600 mt-2">
                          {uploadResult.message}
                        </p>
                      )}
                    </div>
                    {uploadResult.failed_drugs &&
                      uploadResult.failed_drugs.length > 0 && (
                        <div className="mt-3 max-h-16 overflow-auto">
                          <p className="text-xs font-medium text-red-800 mb-1">
                            Failed drugs:
                          </p>
                          <div className="max-h-32 overflow-y-auto">
                            <ul className="text-xs text-red-700 space-y-1">
                              {uploadResult.failed_drugs.map((drug, index) => (
                                <li key={index}>
                                  • {drug.name} : {drug.error}
                                </li>
                              ))}
                            </ul>
                          </div>
                        </div>
                      )}
                  </div>
                </div>
              ) : (
                <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                  <div className="flex items-start space-x-3">
                    <FaCheckCircle className="h-5 w-5 text-green-500 mt-0.5" />
                    <div className="flex-1">
                      <h4 className="text-sm font-medium text-green-800">
                        Upload Successful
                      </h4>
                      <div className="text-sm text-green-700 mt-1">
                        <p>Total processed: {uploadResult.total_processed}</p>
                        <p>
                          Successfully created: {uploadResult.successful_count}{" "}
                          drugs
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end space-x-3 p-6 border-t bg-gray-50">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
          >
            Cancel
          </button>
          <button
            onClick={handleUpload}
            disabled={!file || isUploading}
            className="px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
          >
            {isUploading && (
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
            )}
            <span>{isUploading ? "Uploading..." : "Upload Drugs"}</span>
          </button>
        </div>
      </div>
    </div>
  );
};

export default BranchBulkDrugUploadModal;
